/**
 * Keeping the open itinerary in the URL.
 *
 * Asana O2-54, reported as "I'm getting bounced back". The itinerary detail and
 * edit views were held in a plain `ref` in Templates/my-ohc/itineraries.vue and
 * written nowhere else, so ANY full page load during Edit > Add Site to
 * Itinerary landed you back on the All Itineraries list. Reproduced on
 * production: open an itinerary, apply a filter, load the resulting URL, and the
 * page title goes from the itinerary's name to "Itineraries".
 *
 * Nothing was wrong with the filters — they narrow the list correctly and do not
 * navigate. What made this look like a filter bug is that applying one WRITES a
 * query string, so the URL starts to look like a resumable address of where you
 * are while carrying nothing that can restore you.
 *
 * The triggers are all the ordinary reasons a Nuxt app reloads itself, which is
 * why it read as intermittent: a lazy chunk 404ing after a deploy (this repo has
 * four commits about exactly that), the PWA's "new version available" reload
 * (Asana O2-80), or anything the error boundary catches. Fixing each trigger is
 * endless; making the view survive a reload is not.
 *
 * The mechanism already existed for exactly half the cases — a SHARED itinerary
 * carries `title` and `sites` in the query and restores fine on mount. Only the
 * user's own never did.
 *
 * Keyed by name because that is what the rest of the flow already matches on
 * (`refreshItineraryList` finds the live copy by name, as does the store's
 * updateSingleItinerary). Two itineraries with the same name would open the
 * first; that ambiguity is pre-existing rather than introduced here, and fixing
 * it properly means giving itineraries stable ids.
 */
export interface NamedItinerary {
    name: string;
    [key: string]: unknown;
}

type Query = Record<string, unknown>;

/**
 * The query with the itinerary added, or removed when `name` is null.
 *
 * Copies rather than mutates, and leaves every other param untouched: the filter
 * params (`datesFilters`, `sortBy`, `siteTypeFilters`, ...) are written to the
 * same query by Filters/sites.vue, and dropping those on open or close would
 * silently reset the list someone had just narrowed.
 */
export function withItinerary(query: Query | undefined, name: string | null | undefined): Query {
    const next: Query = { ...(query ?? {}) };
    if (name) next.itinerary = name;
    else delete next.itinerary;
    return next;
}

/**
 * The itinerary the URL names, or null.
 *
 * Null always means "not this time" and never "stop trying": the store hydrates
 * from localStorage on the client, and a logged-in user's list can be replaced
 * again by a later server sync, so the caller runs this against a list that is
 * legitimately empty on the first pass and fills in afterwards.
 *
 * Decodes the name, since it went through the URL to get here and may carry
 * spaces or an ampersand.
 */
export function itineraryFromQuery<T extends NamedItinerary>(list: readonly T[] | undefined, raw: unknown): T | null {
    if (!list?.length || typeof raw !== 'string' || !raw) return null;

    let name = raw;
    try {
        name = decodeURIComponent(raw);
    } catch {
        // A malformed escape sequence throws; the raw value is still worth
        // matching against rather than losing the restore entirely.
    }

    return list.find((item) => item?.name === name) ?? null;
}
